// Extraction orchestration for the Custom Field Builder (browser-side).
//
// Stage 1 ("structure"): read the document into a neutral form model — no FMX
// concepts. PDFs go as page images (scans and digital PDFs get identical
// handling, provenance is exact, and every request stays under the proxy
// body limit by construction), in batches of 3 pages SEQUENTIALLY with a
// rolling summary of the sections found so far — forms have cross-page
// structure, unlike the spreadsheet PDFs pdfExtract.js parallelizes.
//
// Stage 2 ("mapping"): stage-1 JSON + the confirmed scope → the five buckets
// (fields / nativeMappings / prerequisites / unsupportedLogic / flags). Text
// only — no document images.
//
// Both stages route through the admin prompt subsystem (prompts +
// prompt_examples + extraction_runs) as migration_type 'Custom Field
// Builder', so admins can tune them and every run logs token usage + cost.
// Parse failures get ONE automatic retry with a corrective instruction; a
// second failure marks the run 'error' with the raw text stored for admin
// inspection, and nothing downstream sees a half-parsed result.

import { supabase } from '../supabase';
import { claudeFetch, parseClaudeText, CLAUDE_MODEL } from '../apiClient.js';
import {
  getActivePrompt, createExtractionRun, completeExtractionRun,
  getEnabledExamplesForPrompt, incrementExampleUsage,
} from '../db';
import { buildSystemPrompt, sumUsage } from '../promptTemplates';
import { renderPdfPagesToImages } from '../pdfExtract';
import { shrinkImageIfNeeded } from '../equipmentOcr';
import {
  parseJsonLoose, validateStage1Shape, validateStage2Shape,
  mergeStage1Batches, summarizeStage1ForContext, stage1ItemIds,
} from './extractionShapes.js';
import { FIELD_TYPE_POST_VALUES } from './fmxCustomFieldsApi.js';
import { fieldsetLabel } from './fieldsets.js';

export const MIGRATION_TYPE = 'Custom Field Builder';
export const PAGE_CAP = 30;          // cost guard — every page is a vision call
const PAGE_BATCH = 3;                // pages per stage-1 call
// Output budget per stage call. Stage 1 emits ~12 keys per form item, so a
// dense 3-page batch needs real headroom — a response cut off at the cap
// (stop_reason 'max_tokens') can never parse. Doubled again on a truncation
// retry in callWithRetry. max_tokens is a cap, not a spend — unused budget
// costs nothing.
const STAGE_MAX_TOKENS = 16000;

// ── Source ingestion ────────────────────────────────────────────────────────

const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'webp', 'gif'];

export function classifySource(file) {
  const ext = (file?.name || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return 'pdf';
  if (ext === 'docx') return 'docx';
  if (IMAGE_EXTS.includes(ext)) return 'image';
  return null;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Could not read the file.'));
    r.readAsDataURL(file);
  });
}

function dataUrlParts(dataUrl) {
  const comma = dataUrl.indexOf(',');
  const header = dataUrl.slice(0, comma);
  const mediaType = header.slice(header.indexOf(':') + 1, header.indexOf(';'));
  return { mediaType, base64: dataUrl.slice(comma + 1) };
}

/**
 * Turn an upload (or pasted text) into the model-ready source description:
 *   { kind, filename, pageCount,
 *     images: [{ mediaType, base64 }] (pdf/image) | text: string (docx/text) }
 * Enforces the 30-page cap with a plain-language error.
 */
export async function prepareSource({ file, pastedText, onProgress }) {
  if (pastedText != null) {
    const text = String(pastedText).trim();
    if (!text) throw new Error('Paste some form text first.');
    return { kind: 'text', filename: 'pasted-text.txt', pageCount: null, text };
  }
  const kind = classifySource(file);
  if (!kind) throw new Error(`Unsupported file type — use a PDF, image (${IMAGE_EXTS.join('/')}), .docx, or pasted text.`);

  if (kind === 'pdf') {
    onProgress?.('Rendering pages…');
    const dataUrls = await renderPdfPagesToImages(file, (cur, total) => {
      if (total > PAGE_CAP) throw new Error(`This PDF has ${total} pages — the form builder caps at ${PAGE_CAP}. This looks like a report, not a form; if it really is a form, split off the form pages and retry.`);
      onProgress?.('Rendering pages…', { current: cur, total });
    });
    return { kind, filename: file.name, pageCount: dataUrls.length, images: dataUrls.map(dataUrlParts) };
  }
  if (kind === 'image') {
    const { mediaType, base64 } = dataUrlParts(await readFileAsDataUrl(file));
    const shrunk = await shrinkImageIfNeeded({ base64, contentType: mediaType });
    return {
      kind, filename: file.name, pageCount: 1,
      images: [{ mediaType: shrunk.contentType || mediaType, base64: shrunk.base64 }],
    };
  }
  // .docx → mammoth (dynamic import, same pattern the wizard uses for xlsx).
  // Known increment-1 limitation: embedded scanned images inside a .docx are
  // not read — convert those documents to PDF instead.
  onProgress?.('Reading document…');
  const mammoth = await import('mammoth');
  const arrayBuffer = await file.arrayBuffer();
  const { value } = await mammoth.extractRawText({ arrayBuffer });
  if (!value?.trim()) throw new Error('No readable text found in this .docx. If it is a scanned document, convert it to PDF and retry.');
  return { kind, filename: file.name, pageCount: null, text: value };
}

// Store the original in the existing 'pdf-uploads' bucket (name is
// historical; its policies and project-deletion cleanup already work) so the
// session can re-render and re-extract later without re-uploading. Pasted
// text is stored as a .txt so every source is treated uniformly.
// Non-fatal on failure, matching pdfExtract's upload discipline.
export async function uploadSourceToStorage({ file, pastedText, userId }) {
  try {
    const ts = Date.now();
    let blob = file;
    let name = file?.name || 'source';
    if (pastedText != null) {
      blob = new Blob([pastedText], { type: 'text/plain' });
      name = 'pasted-text.txt';
    }
    const key = `${userId || 'anon'}/${ts}_${name.replace(/[^\w.\-]+/g, '_')}`;
    const { error } = await supabase.storage
      .from('pdf-uploads')
      .upload(key, blob, { contentType: blob.type || 'application/octet-stream', upsert: false });
    if (error) {
      console.warn('Source storage upload failed (non-fatal):', error.message);
      return null;
    }
    return key;
  } catch (e) {
    console.warn('Source storage upload exception (non-fatal):', e);
    return null;
  }
}

// Re-materialize a stored original for the source panel of a RESUMED session
// (fresh sessions reuse the in-memory pages from prepareSource). PDFs
// re-render to page images with the existing pdfjs pipeline; images display
// directly; .docx re-extracts through mammoth; pasted text comes back as the
// stored .txt. Returns { kind, pages: [dataUrl…] } or { kind, text }.
export async function loadStoredSource(session) {
  const key = session?.storage_key;
  if (!key) throw new Error('The original document was not stored for this session.');
  const { data: blob, error } = await supabase.storage.from('pdf-uploads').download(key);
  if (error || !blob) throw new Error('Could not download the original document from storage.');
  const kind = session.source_kind;
  if (kind === 'pdf') return { kind, pages: await renderPdfPagesToImages(blob) };
  if (kind === 'image') return { kind, pages: [await readFileAsDataUrl(blob)] };
  if (kind === 'docx') {
    const mammoth = await import('mammoth');
    const { value } = await mammoth.extractRawText({ arrayBuffer: await blob.arrayBuffer() });
    return { kind, text: value || '' };
  }
  return { kind: 'text', text: await blob.text() };
}

// ── Shared call helpers ─────────────────────────────────────────────────────

// /api/claude already retries 429/529 server-side, but only ~3 attempts over
// a few seconds — a real Anthropic overload window outlives that and the
// browser still sees the error. Since a failed stage-1 run throws away every
// batch already extracted (no resume in Milestone 1), transient statuses get
// a second, slower retry layer here before we give up. 502 is the proxy's
// "failed to reach Claude" transport error — same treatment. The ladder is
// deliberately long (~2 min of patience, with progress messaging) because
// observed overload waves outlast a 30-second window.
const TRANSIENT_STATUSES = [429, 502, 529];
const TRANSIENT_RETRY_DELAYS_MS = [3000, 8000, 15000, 30000, 60000];

async function claudeFetchWithBackoff(args, onProgress) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await claudeFetch(args);
    } catch (err) {
      if (!TRANSIENT_STATUSES.includes(err?.status) || attempt >= TRANSIENT_RETRY_DELAYS_MS.length) throw err;
      const waitMs = TRANSIENT_RETRY_DELAYS_MS[attempt];
      onProgress?.(`Claude is busy — retrying in ${Math.round(waitMs / 1000)}s (attempt ${attempt + 2})…`);
      await new Promise(r => setTimeout(r, waitMs));
    }
  }
}

// One retry on a bad response (transient-error retries happen a layer below,
// in claudeFetchWithBackoff). Two distinct failure modes:
//   · truncation — stop_reason 'max_tokens' means the JSON was cut off at the
//     output cap. A truncated response can still brace-slice into something
//     parseable with items silently missing, so truncation is ALWAYS a
//     failure; the retry re-sends unchanged with a doubled budget.
//   · parse failure — the retry appends a corrective instruction.
async function callOnce({ systemPrompt, content, maxTokens, onProgress }) {
  const res = await claudeFetchWithBackoff({
    max_tokens: maxTokens,
    system: systemPrompt,
    messages: [{ role: 'user', content }],
  }, onProgress);
  const truncated = res?.stop_reason === 'max_tokens';
  return { res, truncated, parsed: truncated ? null : parseJsonLoose(parseClaudeText(res)) };
}

async function callWithRetry({ systemPrompt, content, label, onProgress }) {
  const responses = [];
  let attempt = await callOnce({ systemPrompt, content, maxTokens: STAGE_MAX_TOKENS, onProgress });
  responses.push(attempt.res);
  if (!attempt.parsed) {
    const retryContent = attempt.truncated
      ? content
      : (Array.isArray(content) ? content : [{ type: 'text', text: content }]).concat([{
          type: 'text',
          text: 'Your previous response could not be parsed as JSON. Return ONLY the valid JSON object described in the instructions — no prose, no markdown fences, no trailing commentary.',
        }]);
    onProgress?.(attempt.truncated
      ? 'The response was cut off mid-JSON — retrying with a larger output budget…'
      : 'The response was not valid JSON — retrying…');
    attempt = await callOnce({ systemPrompt, content: retryContent, maxTokens: STAGE_MAX_TOKENS * 2, onProgress });
    responses.push(attempt.res);
  }
  if (!attempt.parsed) {
    const err = new Error(
      attempt.truncated
        ? `${label} failed — the AI response was cut off before the JSON completed, even with a doubled output budget (a very dense document can do this). The raw response was saved to the run log for admin inspection.`
        : `${label} failed — the AI did not return valid JSON (after one retry). The raw response was saved to the run log for admin inspection.`
    );
    err.rawText = parseClaudeText(attempt.res)?.slice(0, 4000) || null;
    err.responses = responses;
    throw err;
  }
  return { parsed: attempt.parsed, responses };
}

async function loadPrompt(stage) {
  const prompt = await getActivePrompt(MIGRATION_TYPE, stage);
  if (!prompt?.body) {
    throw new Error(`No active "${stage}" prompt found for ${MIGRATION_TYPE}. Run migration 033 or ask an admin to configure one in Admin Settings → Prompts.`);
  }
  const examples = await getEnabledExamplesForPrompt(prompt.id);
  return { prompt, examples };
}

// ── Stage 1: structural read ────────────────────────────────────────────────

/**
 * Run the stage-1 structural extraction. Returns
 *   { stage1, quarantined, runId, pageCount }.
 * Marks the extraction_runs row 'error' (with raw text) and rethrows on
 * failure — callers show "extraction failed, nothing was created" + retry.
 */
export async function runStage1({ projectId, userId, source, storageKey, onProgress }) {
  const start = Date.now();
  const { prompt, examples } = await loadPrompt('structure');
  const systemPrompt = buildSystemPrompt({
    body: prompt.body,
    vars: { SOURCE_KIND: source.kind },
    examples,
  });

  const run = await createExtractionRun({
    projectId, userId,
    migrationType: MIGRATION_TYPE,
    stage: 'structure',
    storageKey: storageKey || null,
    sourceFilename: source.filename,
    pageCount: source.pageCount,
    promptId: prompt.id,
    promptVersion: prompt.version,
  });

  try {
    let stage1 = null;
    const quarantined = [];
    const allResponses = [];

    if (source.images) {
      if (source.images.length > PAGE_CAP) {
        throw new Error(`This document has ${source.images.length} pages — the form builder caps at ${PAGE_CAP}.`);
      }
      // Sequential batches with rolling context (deliberate departure from
      // pdfExtract's parallel batches — see module header).
      const batches = [];
      for (let i = 0; i < source.images.length; i += PAGE_BATCH) {
        batches.push(source.images.slice(i, i + PAGE_BATCH));
      }
      for (let bi = 0; bi < batches.length; bi++) {
        const firstPage = bi * PAGE_BATCH + 1;
        const lastPage = firstPage + batches[bi].length - 1;
        onProgress?.(`Reading pages ${firstPage}–${lastPage} of ${source.images.length}…`, { current: bi + 1, total: batches.length });
        const content = [
          {
            type: 'text',
            text:
              `Pages ${firstPage}–${lastPage} of a ${source.images.length}-page document (${source.filename}).\n` +
              `Number pages by their position in the whole document (this batch starts at page ${firstPage}).\n` +
              `Sections found on earlier pages (continue them where the document continues — do not re-create them):\n` +
              `${summarizeStage1ForContext(stage1)}\n\n` +
              `Return ONLY the JSON object for the NEW sections/items visible on these pages.`,
          },
          ...batches[bi].map(img => ({
            type: 'image',
            source: { type: 'base64', media_type: img.mediaType, data: img.base64 },
          })),
        ];
        const { parsed, responses } = await callWithRetry({ systemPrompt, content, label: 'Structural read', onProgress });
        allResponses.push(...responses);
        const v = validateStage1Shape(parsed);
        quarantined.push(...v.quarantined);
        stage1 = mergeStage1Batches(stage1, v.stage1);
      }
    } else {
      onProgress?.('Reading the document…');
      const content = [{
        type: 'text',
        text:
          `The complete document text (${source.filename}) follows between the markers. ` +
          `Use "sourceSection" (heading path) for provenance; "sourcePage" stays null.\n` +
          `----- DOCUMENT START -----\n${source.text}\n----- DOCUMENT END -----\n\n` +
          `Return ONLY the JSON object.`,
      }];
      const { parsed, responses } = await callWithRetry({ systemPrompt, content, label: 'Structural read', onProgress });
      allResponses.push(...responses);
      const v = validateStage1Shape(parsed);
      quarantined.push(...v.quarantined);
      stage1 = v.stage1;
    }

    if (examples?.length) incrementExampleUsage(examples.map(e => e.id));
    const usage = sumUsage(allResponses);
    if (run?.id) {
      completeExtractionRun(run.id, {
        status: 'complete',
        resultJson: {
          model: CLAUDE_MODEL,
          sourceKind: source.kind,
          sectionCount: stage1.sections.length,
          itemCount: [...stage1ItemIds(stage1)].length,
          quarantinedCount: quarantined.length,
          stage1,
        },
        durationMs: Date.now() - start,
        inputTokens: usage.inputTokens,
        outputTokens: usage.outputTokens,
        estimatedCostUsd: usage.costUsd,
      });
    }
    return { stage1, quarantined, runId: run?.id || null, pageCount: source.pageCount };
  } catch (err) {
    if (run?.id) {
      completeExtractionRun(run.id, {
        status: 'error',
        error: err?.message || String(err),
        resultJson: err?.rawText ? { rawText: err.rawText } : null,
        durationMs: Date.now() - start,
      });
    }
    throw err;
  }
}

// ── Stage 2: FMX mapping ────────────────────────────────────────────────────

/**
 * Run the stage-2 mapping over a stage-1 result with the confirmed scope.
 * `nativeLabelsByFieldset` (optional) carries the live system-field labels of
 * each target entity form to sharpen the AI's nativeMappings first pass —
 * the deterministic screens re-check regardless.
 * Returns { stage2, quarantined, runId }.
 */
export async function runStage2({
  projectId, userId, stage1, confirmedFieldsets, nativeLabelsByFieldset = {}, sourceFilename, onProgress,
}) {
  const start = Date.now();
  const { prompt, examples } = await loadPrompt('mapping');
  const systemPrompt = buildSystemPrompt({
    body: prompt.body,
    vars: {
      FIELDSETS: confirmedFieldsets.map(f => `${f} (${fieldsetLabel(f)})`).join(', '),
      FIELD_TYPES: FIELD_TYPE_POST_VALUES.join(' | '),
    },
    examples,
  });

  const run = await createExtractionRun({
    projectId, userId,
    migrationType: MIGRATION_TYPE,
    stage: 'mapping',
    sourceFilename: sourceFilename || null,
    pageCount: null,
    promptId: prompt.id,
    promptVersion: prompt.version,
  });

  try {
    onProgress?.('Mapping to FMX fields…');
    const nativeLines = confirmedFieldsets
      .map(f => {
        const labels = nativeLabelsByFieldset[f];
        return labels?.length ? `- ${f}: ${labels.join(', ')}` : null;
      })
      .filter(Boolean);
    const content = [{
      type: 'text',
      text:
        `Confirmed fieldsets: ${confirmedFieldsets.join(', ')}\n\n` +
        (nativeLines.length
          ? `Live built-in (system) fields on the target FMX forms — items matching these are native, not custom:\n${nativeLines.join('\n')}\n\n`
          : '') +
        `The structural read of the form:\n${JSON.stringify(stage1)}\n\n` +
        `Classify every item into the five buckets. Return ONLY the JSON object.`,
    }];
    const { parsed, responses } = await callWithRetry({ systemPrompt, content, label: 'FMX mapping', onProgress });
    const v = validateStage2Shape(parsed, { confirmedFieldsets, stage1Ids: stage1ItemIds(stage1) });

    if (examples?.length) incrementExampleUsage(examples.map(e => e.id));
    const usage = sumUsage(responses);
    if (run?.id) {
      completeExtractionRun(run.id, {
        status: 'complete',
        resultJson: {
          model: CLAUDE_MODEL,
          confirmedFieldsets,
          counts: {
            fields: v.stage2.fields.length,
            nativeMappings: v.stage2.nativeMappings.length,
            prerequisites: v.stage2.prerequisites.length,
            unsupportedLogic: v.stage2.unsupportedLogic.length,
            flags: v.stage2.flags.length,
            quarantined: v.quarantined.length,
          },
          stage2: v.stage2,
        },
        durationMs: Date.now() - start,
        inputTokens: usage.inputTokens,
        outputTokens: usage.outputTokens,
        estimatedCostUsd: usage.costUsd,
      });
    }
    return { stage2: v.stage2, quarantined: v.quarantined, runId: run?.id || null };
  } catch (err) {
    if (run?.id) {
      completeExtractionRun(run.id, {
        status: 'error',
        error: err?.message || String(err),
        resultJson: err?.rawText ? { rawText: err.rawText } : null,
        durationMs: Date.now() - start,
      });
    }
    throw err;
  }
}

// Propose fieldsets from the stage-1 read with simple deterministic keyword
// heuristics — shown as SUGGESTIONS on the scope screen (the human always
// confirms; ambiguity is presented, never silently guessed).
export function suggestFieldsets(stage1) {
  const text = JSON.stringify(stage1 || {}).toLowerCase();
  const suggestions = [];
  const add = (key, why) => { if (!suggestions.some(s => s.key === key)) suggestions.push({ key, why }); };
  if (/work order|work request|maintenance request|repair/.test(text)) add('workRequestDetails', 'looks like a work order / maintenance request form');
  if (/resolution|completed by|work performed|date completed/.test(text)) add('workRequestResolution', 'has a completion / resolution section');
  if (/reservation|facility use|room request|event|rental/.test(text)) add('scheduleRequestDetails', 'looks like a room reservation / facility use form');
  if (/equipment|asset|nameplate|serial number|model number/.test(text)) add('equipmentItems', 'references equipment / asset data');
  if (/inspection|checklist|instruction/.test(text)) add('workTaskInstructionSet', 'looks like an inspection / instruction checklist');
  if (/transportation|vehicle|trip|bus/.test(text)) add('transportationRequestDetails', 'references transportation / trips');
  if (/inventory|stock|supply/.test(text)) add('inventoryItems', 'references inventory / supplies');
  return suggestions;
}
